import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApi, type ApiError, type Briefing } from "../lib/api";
import { useTenant } from "../lib/tenant";
import type { components } from "../lib/api-types";
import ExecutiveExportButton from "../components/ExecutiveExportButton";

type BriefingVerification = components["schemas"]["BriefingVerificationReport"];

const TYPE_LABEL: Record<Briefing["type"], string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  ad_hoc: "Ad-hoc",
};

const AUDIENCE_LABEL: Record<Briefing["audience"], string> = {
  cfo: "CFO",
  ceo: "CEO",
  coo: "COO",
  controller: "Controller",
  all: "All Stakeholders",
};

const STATUS_TONE: Record<Briefing["status"], "good" | "warn" | "bad" | "muted"> = {
  delivered: "good",
  ready: "good",
  generating: "warn",
  queued: "muted",
  failed: "bad",
};

type BriefingDetail = Briefing & {
  highlights?: Array<{
    metric: string;
    direction: "up" | "down" | "flat";
    delta_pct?: number;
    summary: string;
  }>;
  rca?: Array<{ hypothesis: string; confidence: number; evidence_ref?: string }>;
  actions?: Array<{ title: string; impact_eur?: number; owner?: string; due?: string }>;
  owner?: { name: string; role: string; escalation?: string };
  evidence_bundle?: string;
  evidence_id?: string;
  evidence_refs?: string[];
  source_refs?: string[];
  run_id?: string;
  model_route?: string;
  prompt_version?: string;
  evalset_id?: string;
  langfuse_trace?: string;
  citations?: Array<{ label: string; ref: string; evidence_id?: string | null; route?: string | null }>;
};

type DataTrustBlockDetail = {
  code?: string;
  surface?: string;
  tenant_id?: string;
  trust_gate_status?: string;
  quality_threshold?: number;
  next_actions?: string[];
  evidence_refs?: string[];
};

function extractDataTrustBlock(error: unknown): DataTrustBlockDetail | null {
  const apiError = error as ApiError | undefined;
  const details = apiError?.details;
  const candidate = details && typeof details === "object" && "detail" in details
    ? (details as { detail?: unknown }).detail
    : details;
  if (!candidate || typeof candidate !== "object") return null;
  const detail = candidate as DataTrustBlockDetail;
  return detail.code === "data_trust_gate_blocked" ? detail : null;
}

export default function BriefingsPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const [selected, setSelected] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["briefings", effectiveTenantId],
    queryFn: () => api.get<Briefing[]>("/briefings"),
    refetchInterval: 60_000,
  });

  const detail = useQuery({
    queryKey: ["briefings", effectiveTenantId, selected],
    queryFn: () => api.get<BriefingDetail>(`/briefings/${selected}`),
    enabled: !!selected,
  });

  const verification = useQuery({
    queryKey: ["briefings", effectiveTenantId, selected, "verification"],
    queryFn: () => api.get<BriefingVerification>(`/briefings/${selected}/verification`),
    enabled: !!selected,
  });
  const listBlock = extractDataTrustBlock(list.error);
  const detailBlock = extractDataTrustBlock(detail.error);

  const sorted = useMemo(() => {
    return [...(list.data ?? [])].sort((a, b) =>
      b.created_at.localeCompare(a.created_at),
    );
  }, [list.data]);

  return (
    <div className="page briefings-page">
      <h1>Briefings</h1>
      <p className="subtitle">
        C-Level-Briefings nach FR-07 — Was passiert · Warum · Was tun · Owner · Evidenz.
      </p>

      <div className="briefings-layout">
        <aside className="briefings-list">
          {list.isLoading && <p>Lade…</p>}
          {listBlock && <DataTrustBlocked detail={listBlock} />}
          {list.error && !listBlock && <p className="error">Fehler beim Laden.</p>}
          {sorted.length === 0 && !list.isLoading && !listBlock && (
            <div className="empty-state">
              <p>Noch keine Briefings.</p>
              <p className="small">
                Briefings werden vom C-Level-Briefing-Agent (Phase v3, CP-114)
                erzeugt. Sobald Quellen + KPIs angebunden sind, erscheint hier
                der erste Daily-Run.
              </p>
            </div>
          )}
          {!listBlock && <ul className="run-list">
            {sorted.map((b) => (
              <li
                key={b.id}
                className={`run-item ${selected === b.id ? "expanded" : ""}`}
              >
                <button
                  type="button"
                  className="run-row"
                  onClick={() => setSelected(b.id)}
                >
                  <span className={`pill ${STATUS_TONE[b.status]}`}>
                    {b.status}
                  </span>
                  <span className="pill muted">{TYPE_LABEL[b.type]}</span>
                  <span className="pill accent">{AUDIENCE_LABEL[b.audience]}</span>
                  <span className="grow" />
                  <span className="small muted">
                    {new Date(b.created_at).toLocaleString()}
                  </span>
                </button>
                <Link to={`/app/briefings/${encodeURIComponent(b.id)}`} className="btn ghost">
                  Detail
                </Link>
              </li>
            ))}
          </ul>}
        </aside>

        <section className="briefings-detail">
          {!selected && (
            <div className="empty-state">
              <p>Wähle links ein Briefing aus.</p>
            </div>
          )}
          {selected && detail.isLoading && <p>Lade Details…</p>}
          {selected && detailBlock && <DataTrustBlocked detail={detailBlock} />}
          {selected && detail.error && !detailBlock && (
            <p className="error">Detail nicht verfügbar.</p>
          )}
          {selected && detail.data && (
            <BriefingFiveColumn briefing={detail.data} verification={verification.data} />
          )}
        </section>
      </div>
    </div>
  );
}

function DataTrustBlocked({ detail }: { detail: DataTrustBlockDetail }) {
  return (
    <section className="lineage-card briefing-trust-block">
      <div className="lineage-head">
        <div>
          <h3>Data Trust Gate blockiert Briefings</h3>
          <p className="muted small">
            Tenant {detail.tenant_id ?? "unbekannt"} · Status {detail.trust_gate_status ?? "blocked"}
            {typeof detail.quality_threshold === "number" && <> · Schwelle {detail.quality_threshold}/100</>}
          </p>
        </div>
        <span className="pill bad">blocked</span>
      </div>
      <p>
        Vorhandene Executive Outputs werden nicht gelistet, bis eine vertrauenswürdige Quelle und ein Quality Report
        im Tenant vorliegen.
      </p>
      {(detail.next_actions ?? []).length > 0 && (
        <ul className="lineage-list">
          {(detail.next_actions ?? []).slice(0, 4).map((action) => (
            <li key={action}>
              <span className="pill warn">next</span>
              <span>{action}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="brief-cta">
        <Link to="/app/sources" className="btn ghost">Quellen prüfen</Link>
        <Link to="/app/command-center" className="btn ghost">Gate im Command Center</Link>
      </div>
    </section>
  );
}

function BriefingFiveColumn({ briefing, verification }: { briefing: BriefingDetail; verification?: BriefingVerification }) {
  const fmtEur = (n?: number) =>
    typeof n !== "number" ? "–" : new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(n);

  return (
    <article className="briefing-five">
      <header className="briefing-head">
        <div>
          <h2>
            {TYPE_LABEL[briefing.type]} · {AUDIENCE_LABEL[briefing.audience]}
          </h2>
          <div className="small muted">
            {new Date(briefing.created_at).toLocaleString()} · {briefing.id}
          </div>
        </div>
        <div className="briefing-links">
          <ExecutiveExportButton
            compact
            label="Evidence Export"
            type="evidence_package"
            format="pdf"
            audience={briefing.audience}
            briefingId={briefing.id}
            scope={["briefing", briefing.id]}
          />
          {briefing.markdown_url && (
            <a href={briefing.markdown_url} className="btn ghost" target="_blank" rel="noreferrer">
              Markdown
            </a>
          )}
          {briefing.pdf_url && (
            <a href={briefing.pdf_url} className="btn ghost" target="_blank" rel="noreferrer">
              PDF
            </a>
          )}
        </div>
      </header>

      {verification && (
        <section className={`lineage-card briefing-verification ${verification.status}`}>
          <div className="lineage-head">
            <div>
              <h5>Citation Gate</h5>
              <span className="muted small">
                {verification.resolved_citation_count}/{verification.citation_count} Citations · {verification.audit_namespace}
              </span>
            </div>
            <span className={`pill ${verification.status === "pass" ? "good" : verification.status === "warning" ? "warn" : "bad"}`}>
              {verification.status}
            </span>
          </div>
          <div className="stat-grid compact">
            <div><span>Local Model</span><strong>{verification.local_model ? "ja" : "nein"}</strong></div>
            <div><span>Missing</span><strong>{verification.missing_citation_count}</strong></div>
            <div><span>Evidence</span><strong>{verification.evidence_id ?? "none"}</strong></div>
            <div><span>Run</span><strong>{verification.run_id ?? "none"}</strong></div>
          </div>
          <ul className="lineage-list">
            {(verification.checks ?? []).map((check) => (
              <li key={check.id}>
                <span className={`pill ${check.status === "pass" ? "good" : check.status === "warning" ? "warn" : "bad"}`}>{check.status}</span>
                <div>
                  <strong>{check.title}</strong>
                  <p className="muted small">{check.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="five-cols">
        <div className="col">
          <div className="col-title">Was ist passiert</div>
          {(briefing.highlights ?? []).length === 0 && (
            <p className="muted small">Keine Bewegungen erfasst.</p>
          )}
          <ul className="col-list">
            {(briefing.highlights ?? []).map((h, i) => (
              <li key={i}>
                <strong>{h.metric}</strong>
                {typeof h.delta_pct === "number" && (
                  <span className={`pill ${h.direction === "up" ? "good" : h.direction === "down" ? "bad" : "muted"}`}>
                    {h.delta_pct > 0 ? "+" : ""}{h.delta_pct.toFixed(1)}%
                  </span>
                )}
                <div className="small muted">{h.summary}</div>
              </li>
            ))}
          </ul>
        </div>

        <div className="col">
          <div className="col-title">Warum</div>
          {(briefing.rca ?? []).length === 0 && (
            <p className="muted small">RCA noch ausstehend.</p>
          )}
          <ul className="col-list">
            {(briefing.rca ?? []).map((r, i) => (
              <li key={i}>
                <div>{r.hypothesis}</div>
                <div className="small muted">
                  Konfidenz {(r.confidence * 100).toFixed(0)}%
                  {r.evidence_ref && (
                    <> · <a href={r.evidence_ref}>Evidenz</a></>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="col">
          <div className="col-title">Was tun</div>
          {(briefing.actions ?? []).length === 0 && (
            <p className="muted small">Keine Maßnahmen vorgeschlagen.</p>
          )}
          <ul className="col-list">
            {(briefing.actions ?? []).map((a, i) => (
              <li key={i}>
                <strong>{a.title}</strong>
                <div className="small muted">
                  Impact {fmtEur(a.impact_eur)}
                  {a.due && <> · fällig {a.due}</>}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="col">
          <div className="col-title">Owner</div>
          {briefing.owner ? (
            <div>
              <strong>{briefing.owner.name}</strong>
              <div className="small muted">{briefing.owner.role}</div>
              {briefing.owner.escalation && (
                <div className="small muted">Eskalation: {briefing.owner.escalation}</div>
              )}
            </div>
          ) : (
            <p className="muted small">Owner nicht zugewiesen.</p>
          )}
        </div>

        <div className="col">
          <div className="col-title">Evidenz</div>
          <div className="small muted">
            {briefing.model_route ?? "local"} · {briefing.evalset_id ?? "citation-gate"}
          </div>
          <ul className="col-list">
            {briefing.evidence_bundle && (
              <li>
                <a href={briefing.evidence_bundle} target="_blank" rel="noreferrer">
                  Evidence-Bundle
                </a>
              </li>
            )}
            {briefing.langfuse_trace && (
              <li>
                <a href={briefing.langfuse_trace} target="_blank" rel="noreferrer">
                  Langfuse-Trace
                </a>
              </li>
            )}
            {briefing.evidence_id && (
              <li>
                <a href={`/app/agents?evidence=${encodeURIComponent(briefing.evidence_id)}`}>
                  Evidence prüfen
                </a>
              </li>
            )}
            {briefing.run_id && (
              <li>
                <a href={`/app/agents?run=${encodeURIComponent(briefing.run_id)}`}>
                  Agent Run
                </a>
              </li>
            )}
            {(briefing.citations ?? []).map((citation) => (
              <li key={`${citation.label}:${citation.ref}`}>
                {citation.route ? (
                  <a href={citation.route}>{citation.label}</a>
                ) : (
                  <span>{citation.label}</span>
                )}
                <div className="small muted">{citation.ref}</div>
              </li>
            ))}
            {!briefing.evidence_bundle && !briefing.langfuse_trace && !(briefing.citations ?? []).length && (
              <li className="muted small">Keine verlinkten Artefakte.</li>
            )}
          </ul>
        </div>
      </div>
    </article>
  );
}
